import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { vacationRequestService } from '../../services/vacationRequestService';
import type { VacationRequestCreate } from '../../models/vacation.types';
import { DRAFT_GRACE_MINUTES } from '../../utils/vacationRequestStatus';
import { VACATION_BALANCE_QUERY_KEY } from './useVacationBalanceQuery';
import { VACATION_REQUESTS_QUERY_KEY } from './useMyVacationRequestsQuery';
import { registerBalanceRestore } from './useVacationBalanceRestores';

export function useVacationRequestMutations() {
    const queryClient = useQueryClient();
    const toast = useToast();

    const createRequest = useMutation({
        retry: false,
        mutationFn: (data: VacationRequestCreate) => vacationRequestService.create(data),
        onSuccess: (_id, variables) => {
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });
            toast.success(
                'Solicitação registrada',
                `Você tem ${DRAFT_GRACE_MINUTES} minutos para excluí-la ou confirmá-la antes de ela seguir automaticamente para o gestor.`
            );
        },
        onError: (error: Error) => {
            console.error('Erro ao criar solicitação de férias:', error);
            toast.error('Não foi possível enviar a solicitação', error.message);
        },
    });

    const deleteRequest = useMutation({
        retry: false,
        mutationFn: (input: {
            requestId: string;
            collaboratorId: string;
            collaboratorEmail: string;
            reservedDays: number;
        }) => vacationRequestService.deleteDraft(input),
        onSuccess: (_void, variables) => {
            registerBalanceRestore(queryClient, {
                collaboratorId: variables.collaboratorId,
                requestId: variables.requestId,
                days: variables.reservedDays,
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });
            toast.success(
                'Solicitação excluída',
                'Seu pedido foi removido e o gestor não será notificado.'
            );
        },
        onError: (error: Error) => {
            console.error('Erro ao excluir solicitação de férias:', error);
            toast.error('Não foi possível excluir a solicitação', error.message);
        },
    });

    const resendClientEmail = useMutation({
        retry: false,
        mutationFn: (input: { requestId: string; email: string }) =>
            vacationRequestService.resendClientEmail(input),
        onSuccess: (_void, variables) => {
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY],
            });
            toast.success('E-mail reenviado', `Link reenviado para ${variables.email}`);
        },
        onError: (error: Error) => {
            console.error('Erro ao reenviar e-mail do cliente:', error);
            toast.error('Não foi possível reenviar o e-mail', error.message);
        },
    });

    const confirmRequest = useMutation({
        retry: false,
        mutationFn: (input: {
            requestId: string;
            collaboratorEmail: string;
            collaboratorId: string;
            silent?: boolean;
        }) => vacationRequestService.confirmDraft({ requestId: input.requestId }),
        onSuccess: (_void, variables) => {
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });
            toast.success('Solicitação enviada', 'Seu gestor já foi notificado.');
        },
        onError: (error: Error, variables) => {
            console.error('Erro ao confirmar solicitação de férias:', error);
            if (variables.silent) return;
            toast.error('Não foi possível confirmar a solicitação', error.message);
        },
    });

    const submitCorrection = useMutation({
        retry: false,
        mutationFn: (input: {
            requestId: string;
            collaboratorEmail: string;
            collaboratorId: string;
            corrections: Array<{ order: number; startDate: string; endDate: string }>;
        }) =>
            vacationRequestService.submitCorrection({
                requestId: input.requestId,
                corrections: input.corrections,
            }),
        onSuccess: (_void, variables) => {
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });
            toast.success('Correção enviada', 'A nova data segue para o gestor aprovar.');
        },
        onError: (error: Error) => {
            console.error('Erro ao enviar correção:', error);
            toast.error('Não foi possível enviar a correção', error.message);
        },
    });

    return { createRequest, deleteRequest, resendClientEmail, confirmRequest, submitCorrection };
}
